<template>
  <form @submit.prevent="submitSpecialLeaveFormForm" novalidate>
    <div class="row">
      <div class="col-lg-4 col-lg-4 col-sm-12">
        <Datepicker
            v-model="formSpecialLeaveData.startDate"
            :required="true"
            :label="$t('holiday.first_day_of_vacation')"
            position="down"
            :validation="vSpecialLeave$.startDate"
            :messages="vSpecialLeave$.startDate.required.$message"/>
      </div>
      <div class="col-lg-4 col-lg-4 col-sm-12">
        <Datepicker
            v-model="formSpecialLeaveData.endDate"
            :required="true"
            :label="$t('holiday.last_day_of_vacation')"
            position="down"
            :validation="vSpecialLeave$.endDate"
            :messages="vSpecialLeave$.endDate.required.$message"/>
      </div>
      <div class="col-lg-12">
        <TextEditor v-model="formSpecialLeaveData.reasonForLeaving"
                    @update:model-value="formSpecialLeaveData.reasonForLeaving = $event"
                    :inputStyle="{ height: '200px'}"
                    :label="$t('holiday.reason_for_application')"/>
      </div>
      <div class="d-flex justify-content-center w-100 mt-3">
        <button class="btn btn-primary" type="submit">{{ $t('general.button.save') }}</button>
      </div>
    </div>
  </form>
</template>

<script setup>
import holidayService from '@/api/Holiday';
import Datepicker from '@/components/picker/Datepicker.vue';
import TextEditor from '@/components/TextEditor.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { requiredRule } from '@/shared/validators/validationRules';
import Form from '@/views/App/Course/PrivateCoachingModule/Form.vue';
import useVuelidate from '@vuelidate/core';
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t }        = useI18n();
const isSubmitting = ref(false);
const emit         = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  specialLeaveId: {
    type: Number,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formSpecialLeaveData  = ref({
  startDate: '',
  endDate: '',
  reasonForLeaving: '',
});
const formSpecialLeaveRules = ref({
  startDate: requiredRule(t),
  endDate: requiredRule(t),
});

const computedRules  = computed(() => formSpecialLeaveRules.value);
const vSpecialLeave$ = useVuelidate(computedRules, formSpecialLeaveData);

async function submitSpecialLeaveFormForm() {
  vSpecialLeave$.value.$touch();
  if (vSpecialLeave$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (props.specialLeaveId) {
      await holidayService.putSpecialLeave(formSpecialLeaveData.value, props.specialLeaveId);
    } else {
      await holidayService.postSpecialLeave(formSpecialLeaveData.value);
    }

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getSpecialLeaveData(specialLeaveId) {
  try {
    CloseToast();
    const { data }             = await holidayService.getHolidayDetails(+specialLeaveId);
    formSpecialLeaveData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  if (props.specialLeaveId) {
    await getSpecialLeaveData(props.specialLeaveId);
  }
});

</script>
<style scoped>

</style>